import moment from 'moment';
import { maxBy } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCoreSiteId } from '../../settings/reducer';
import {
  selectDetergentPods,
  selectDetergentReadings,
  selectDailyDetergentReadings,
} from '../reducer';
import { getDetergentReadings, getDailyDetergentReadings } from '../actions';
import { generateToastr } from '../../../core/toastMessages';
import DetergentTracker from './DetergentTracker';
import { selectMyChartsWashCounts } from '../../overview/reducer';
import { selectHourlyWashCounts } from '../../wash-counts/reducer';
import { getOverviewWashCounts } from '../../overview/actions';
import { getWashCounts } from '../../overview/actions';
import Loader from '../../../core/components/Loader';
import { fetchHourlyWashCounts } from '../../wash-counts/actions';

interface Props {
  tabType: any;
  detergents: any;
  dayRange?: number;
}

// Both readings endpoints group by detergentId, so these maps are keyed by
// detergentId — never by container id. Scoping is by the current pod's
// detergentIds because the daily fetch requests every detergentId on the site
// and therefore returns sibling pods' detergents too.
export const mergeDetergentReadings = (
  dailyReadings: Record<string, any[]>,
  todaysReadings: Record<string, any[]>,
  podDetergentIds: any[]
): Record<string, any[]> => {
  const idSet = new Set(podDetergentIds.map(String));
  const inPod = Object.entries(dailyReadings || {}).filter(([key]) => idSet.has(String(key)));

  try {
    return Object.fromEntries(
      inPod.map(([key, value]): [string, any[]] => {
        const dailyArray = value as any[];
        // readings for today
        const todaysArray = todaysReadings?.[key];

        // if no readings, return daily
        if (!todaysArray?.length) return [key, dailyArray];

        const latestToday = maxBy(todaysArray, (r: any) => r.id);
        const firstDaily = dailyArray[0];

        if (firstDaily && moment(firstDaily.timestamp).isSame(moment(), 'day')) {
          return [key, [latestToday, ...dailyArray.slice(1)]];
        }
        return [key, [latestToday, ...dailyArray]];
      })
    );
  } catch (e) {
    console.error('Error parsing todays detergent readings: ', e);
    return Object.fromEntries(inPod) as Record<string, any[]>;
  }
};

const DetergentTrackerDataGetter = (props: Props) => {
  const dispatch = useDispatch();
  const siteId = useSelector(selectCoreSiteId);
  const detergentPods = useSelector(selectDetergentPods);
  const overviewWashCounts = useSelector(selectMyChartsWashCounts);
  const hourlyWashCounts = useSelector(selectHourlyWashCounts);
  const range = props.dayRange ?? 7;
  const untilDate = moment().endOf('day').toISOString();
  const fromDate = moment().subtract(range, 'days').startOf('day').toISOString();
  // The overview endpoint treats `date` as the start of the window and returns
  // every day from it through today. The chart plots `range` points starting
  // range - 1 days ago, so ask for exactly that span.
  const washCountsFromDate = moment()
    .subtract(range - 1, 'days')
    .startOf('day')
    .toISOString();
  const startToday = moment().startOf('day').toISOString();
  const endToday = moment().endOf('day').toISOString();
  const detergentReadingsData = useSelector(selectDailyDetergentReadings);
  const todaysDetergentReadingsData = useSelector(selectDetergentReadings);
  const detergentIds = props.detergents?.map((container: { id: any }) => container.id) || [];
  const currentDetergentPod = detergentPods.find((pod: any) => pod.title === props.tabType);
  const detergentContainerIds = useMemo(
    () =>
      currentDetergentPod?.detergentContainers?.map((container: { id: any }) => container.id) || [],
    [detergentPods, props.tabType]
  );
  // Readings come back grouped by detergentId, not container id, so the chart
  // must be scoped by the pod's detergentIds.
  const podDetergentIds = useMemo(
    () =>
      currentDetergentPod?.detergentContainers?.map(
        (container: { detergentId: any }) => container.detergentId
      ) || [],
    [detergentPods, props.tabType]
  );

  const fetchDetergentReadings = () => {
    dispatch(
      getDetergentReadings({
        fromDate: startToday,
        untilDate: endToday,
        detergentContainerIds,
        detergentIds,
      })
    );
    dispatch(getDailyDetergentReadings({ fromDate, untilDate, detergentIds }));
  };
  const getHourlyWashCounts = () => {
    dispatch(
      fetchHourlyWashCounts(
        moment(startToday).format('YYYY-MM-DD'),
        moment(endToday).format('YYYY-MM-DD')
      )
    );
  };
  const fetchWashCounts = () => {
    dispatch(getOverviewWashCounts(washCountsFromDate));
  };
  useEffect(fetchDetergentReadings, [siteId, detergentPods, props.tabType, props.dayRange]);

  const mergedDetergentData = useMemo(
    () =>
      mergeDetergentReadings(detergentReadingsData, todaysDetergentReadingsData, podDetergentIds),
    [detergentReadingsData, todaysDetergentReadingsData, podDetergentIds]
  );

  useEffect(fetchWashCounts, [props.dayRange, siteId]);
  useEffect(() => {
    getHourlyWashCounts();
  }, [siteId]);

  return (
    <>
      {detergentContainerIds?.length > 0 ? (
        <DetergentTracker
          chartData={mergedDetergentData}
          detergents={props.detergents}
          washCounts={overviewWashCounts}
          hourlyWashCounts={hourlyWashCounts}
          dayRange={range}
        />
      ) : (
        <Loader />
      )}
    </>
  );
};

export default DetergentTrackerDataGetter;
